<template>
  <div class="m-reporting-dashboard">
    <m-headline :title="$t('dashboard.tabs.reporting.title')" />
    <div v-if="loading" class="d-flex justify-center my-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <div v-else class="grid">
      <div class="gutter-sizer"></div>
      <div class="grid-sizer"></div>
      <m-dashboard-widget
        v-for="(widget, index) in widgets"
        :key="`widget-${index}`"
        ref="widgets"
        :ritual="ritual"
        :value="widget.dashboardWidget"
        class="m-dashboard-widget grid-item"
      />
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import Packery from 'packery';
import Draggabilly from 'draggabilly';
import { DasboardsActions } from '@/store/dashboards/types';
import { MDashboardWidget } from '@/components/Dashboard/MDashboardWidget';
import { IDashboardSettings, IDashboardWidget } from '@mentessa/types';
import MHeadline from '@/components/MHeadline';

export default Vue.extend({
  name: 'MReportingDashboard',
  components: {
    MHeadline,
    MDashboardWidget,
  },
  data: () => ({
    packery: null as Packery | null,
    dashboardSettings: undefined as IDashboardSettings | undefined,
    loading: false,
  }),
  computed: {
    ritual() {
      return this.dashboardSettings?.ritual;
    },
    widgets(): Array<{ dashboardWidget: IDashboardWidget }> {
      console.log('Computing widgets, loading:', this.loading);
      console.log('Dashboard settings:', this.dashboardSettings);

      if (this.loading || !this.dashboardSettings) {
        return [];
      }

      const widgets =
        this.dashboardSettings?.dashboard?.dashboardWidgets?.map((dashboardWidget) => {
          console.log('Processing widget:', dashboardWidget);
          return {
            dashboardWidget,
          };
        }) ?? [];

      console.log('Final widgets array:', widgets);
      return widgets;
    },
  },
  async mounted() {
    await this.$nextTick();

    const packery = new Packery('.grid', {
      columnWidth: '.grid-sizer',
      itemSelector: '.grid-item',
      gutter: '.gutter-sizer',
      percentPosition: true,
    });

    this.packery = packery;
    await this.reload();
  },
  beforeDestroy() {
    if (this.packery) {
      this.packery.destroy();
      this.packery = null;
    }
  },
  methods: {
    async reload() {
      console.log('Starting reload...');
      try {
        this.loading = true;
        this.dashboardSettings = await this.$store.dispatch({
          type: `dashboards/${DasboardsActions.LoadDashboardGlobal}`,
          tenantRef: undefined, // Will use current tenant
          ids: '4,358',
        });
        console.log('Dashboard loaded successfully:', this.dashboardSettings);
      } catch (error) {
        console.error('Failed to load reporting dashboard:', error);
      } finally {
        this.loading = false;
      }

      this.$nextTick(() => {
        if (!this.packery || !this.dashboardSettings) return;

        this.packery.reloadItems();
        if (!this.dashboardSettings.attributes?.disableDragAndDrop) {
          this.packery.getItemElements().forEach((itemElem) => {
            const draggabilly = new Draggabilly(itemElem);
            if (this.packery) {
              this.packery.bindDraggabillyEvents(draggabilly);
            }
          });
        }
        this.packery.layout();
      });
    },
  },
});
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/settings/_variables';

.m-reporting-dashboard {
  min-height: 400px;
}

.grid {
  max-width: 940px;
}

/* clear fix */
.grid:after {
  content: '';
  display: block;
  clear: both;
}

.grid-sizer,
.grid-item {
  width: calc(50% - 16px);
}

.gutter-sizer {
  width: 16px;
}

.grid-item {
  float: left;

  &.is-dragging,
  &.is-positioning-post-drag {
    z-index: 2;
  }

  &__x2 {
    width: 100%;
  }
}

.grid-item--large {
  z-index: 2;
  width: calc(100% - 16px);
  height: 100%;
}

.m-dashboard-widget {
  min-height: 130px;
  max-height: 130px;
  height: 130px;
  max-width: 450px;
  min-width: 440px;

  &__col-2 {
    max-height: 276px;
    height: 276px;
  }

  &__col-3 {
    max-height: 422px;
    height: 422px;
  }

  &__col-4 {
    max-height: 568px;
    height: 568px;
  }

  &__row-2 {
    max-width: calc(100% - 20px);
    width: 100%;
  }

  border: 1px solid var(--v-beige-base);
  border-radius: 16px;
  background: var(--v-white-base);
  padding: 24px;
}

@media #{map-get($display-breakpoints, 'xs-only')} {
  .grid-sizer,
  .grid-item {
    width: calc(100% - 16px);
  }
}

@media #{map-get($display-breakpoints, 'sm-only')} {
  .grid-sizer,
  .grid-item {
    width: calc(50% - 16px);
  }
}
</style>
